import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
    paymentInitializationFailed,
    stripePaymentInitialized,
    StripePaymentInitializedPayload,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useNavigate } from "react-router-dom";
import routes from "../../../../../routes/routes";
import { useInitializeStripePayment } from "../../../../../redux/flows/initializeStripePaymentFlow";
import LoaderOverlay from "../../../../layout/components/LoaderOverlay";
import { useInitializeVoucherStripePayment } from "../../../../../redux/flows/initializeVoucherStripePaymentFlow";
import { useInitializeSubscriptionStripePayment } from "../../../../../redux/flows/initializeSubscriptionStripePaymentFlow";
import Logger from "../../../../../redux/helpers/logger";

/**
 * Hands the customer over to Stripe's hosted checkout page.
 *
 * The backend returns the Checkout Session's own URL (`session.url`) as
 * `checkoutFrontendUrl`, so the redirect is a plain navigation. It used to go
 * through `stripe.redirectToCheckout`, which is deprecated, needs the matching
 * publishable key in the browser, and reports failure by RESOLVING with
 * `{ error }` rather than throwing. The call was not awaited, so a failed
 * redirect was silent — and this component had already swapped its loader for
 * an empty `<section>`: a white screen with nothing to press.
 *
 * Until the browser leaves, the loader stays up. A failed initialisation — or
 * a return to this page from the back/forward cache — is reported through
 * `paymentInitializationFailed`, which every parent turns into
 * `InitializationErrorInfo`.
 */
const StripePayment = ({ service }: { service: string }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const initializeVoucherStripePayment = useInitializeVoucherStripePayment();
    const initializeStripePayment = useInitializeStripePayment({ service });
    const initializeSubscriptionStripePayment = useInitializeSubscriptionStripePayment();
    const paymentInitilaized = useRef(false);

    useEffect(() => {
        const manageStripePayment = async () => {
            let response: any;
            if (service === "voucher") {
                response = (
                    (await initializeVoucherStripePayment()) as StripePaymentInitializedPayload[]
                )?.[0];
            } else if (service === "subscription") {
                response = await initializeSubscriptionStripePayment();
            } else {
                response = (
                    (await initializeStripePayment()) as StripePaymentInitializedPayload[]
                )?.[0];
            }

            // Handle NO_PAYMENT case (voucher reduced price to 0)
            if (response?.skipCheckout && service === "subscription") {
                navigate(routes.keyHandoverSubscriptionSuccessPath);
                return;
            }

            if (!response?.checkoutFrontendUrl || !response.token) {
                dispatch(paymentInitializationFailed({}));
                return;
            }

            dispatch(stripePaymentInitialized(response));
            window.location.assign(response.checkoutFrontendUrl);
        };

        // In React Strict Mode, this function is called twice during development.
        // To prevent the payment initialization from running multiple times, we use a ref.
        // In production, this function will only be called once, so using a ref will not cause any issues.
        if (!paymentInitilaized.current) {
            paymentInitilaized.current = true;
            manageStripePayment().catch((error) => {
                // The init flows catch their own request errors; anything that
                // still escapes must reach the error panel, not an endless loader.
                Logger.for("StripePayment").error(error);
                dispatch(paymentInitializationFailed({}));
            });
        }
    }, [
        dispatch,
        initializeStripePayment,
        initializeSubscriptionStripePayment,
        initializeVoucherStripePayment,
        navigate,
        service,
    ]);

    // Browser "back" from Stripe's page can restore this page from the
    // back/forward cache exactly as it was left: frozen on the loader, with no
    // effect re-running. Turn that into the failure panel, whose "try again"
    // starts a fresh checkout.
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                dispatch(paymentInitializationFailed({}));
            }
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, [dispatch]);

    return (
        <div className="h-full box-border pt-4">
            <LoaderOverlay open={true} />
        </div>
    );
};

export default StripePayment;
